import Image from 'next/image'
import Section from 'components/common/Section'
import { EnabledWatchDemoButton } from 'components/WatchDemoButton'
import DemoVideoModal from 'components/DemoVideoModal'
import { Marketing } from 'designsystem/Text'
import { Button } from 'designsystem/Button'
import ExternalIcon from 'icons/ExternalIcon'
import { useModal } from 'react-modal-hook'
import { ComponentHeroCentered } from 'types/generated/contentful'
import { useThemeColor } from './ThemeContext'

export const HeroCentered = ({
  headline,
  subheadline,
  icon,
  badge,
  primaryCtaLabel,
  primaryCtaUrl,
  secondaryCtaLabel,
  secondaryCtaUrl,
  secondaryCtaIcon,
  fullWidthImage,
  videoUrl,
}: ComponentHeroCentered): JSX.Element => {
  const themeColor = useThemeColor()
  const [showDemoVideoModal, hideDemoVideoModal] = useModal(() => {
    if (!videoUrl) return null
    return <DemoVideoModal videoUrl={videoUrl} hide={hideDemoVideoModal} />
  })

  return (
    <Section verticalPadding='first' bgColor='gray'>
      <div className='text-left sm:text-center'>
        {icon && (
          <div className='w-14 sm:w-32  sm:mx-auto mb-16'>
            <Image
              src={icon.url!}
              alt={icon.title!}
              width={icon.width!}
              height={icon.height!}
            />
          </div>
        )}

        {badge && (
          <div className='mb-4'>
            <span className='px-1.5 py-1 border border-neutral-25 rounded-6 font-demibold text-15'>
              {badge}
            </span>
          </div>
        )}

        <Marketing.H1>{headline}</Marketing.H1>

        <Marketing.SubHeadline className='spacer-3xs max-w-3xl'>
          {subheadline}
        </Marketing.SubHeadline>

        {(primaryCtaLabel || secondaryCtaLabel) && (
          <div className='flex flex-col items-center justify-center gap-8 mt-14 sm:flex-row'>
            {primaryCtaLabel && (
              <a href={primaryCtaUrl!}>
                <Button
                  color='blue'
                  pageType='marketing'
                  hasArrow
                  tabIndex={-1}
                >
                  {primaryCtaLabel}
                </Button>
              </a>
            )}

            {secondaryCtaLabel && (
              <a
                href={secondaryCtaUrl!}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-1.5 text-18 font-demibold text-neutral-60 hover:text-neutral'
              >
                {secondaryCtaIcon && (
                  <div className='w-6 h-6 relative'>
                    <Image
                      fill
                      src={secondaryCtaIcon.url!}
                      alt={secondaryCtaIcon.title!}
                    />
                  </div>
                )}
                {secondaryCtaLabel}
                <ExternalIcon />
              </a>
            )}
          </div>
        )}

        {fullWidthImage && (
          <div className='-mx-4 mt-16 lg:mt-36'>
            <Image
              src={fullWidthImage.url!}
              alt={fullWidthImage.title!}
              width={fullWidthImage.width!}
              height={fullWidthImage.height!}
            />
          </div>
        )}

        {videoUrl && (
          <div className='flex justify-center'>
            <EnabledWatchDemoButton
              color={themeColor}
              className='spacer-sm'
              onClick={showDemoVideoModal}
            >
              Watch the demo
            </EnabledWatchDemoButton>
          </div>
        )}
      </div>
    </Section>
  )
}
